import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { StorageService } from '@metromarkets/sdk';

import { ASSISTED_SALES_LOGIN_REDIRECT_ROUTE, FeatureToggleEnum, NUMBER_1 } from '~shared/constants';
import { AccountInfoService } from '~app/core/modules/identity/services/account-info.service';
import { filter, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { ClearAddresses } from '~app/modules/checkout/store/addresses';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { State } from '~app/modules/checkout/store';
import { CHECKOUT_LOCALSTORAGE_KEY } from '~app/modules/checkout/constants';
import {
  ACCOUNT_TYPE_KEY,
  EXPONEA_SESSION_UPDATE_LOCAL_STORAGE_KEY,
  HAS_DIGITAL_IDENTITY_KEY,
  IMPERSONATED_CUSTOMER_FULLNAME_KEY,
  PHONE_NUMBER_LOCAL_STORAGE_KEY,
  SESSION_STORAGE_CUSTOMER_BUSINESS_CDM_ID_KEY,
  SESSION_STORAGE_CUSTOMER_CDM_ID_KEY,
} from '~app/core/modules/identity/constants/identity.constant';

import { AuthorizationHttpService } from './authorization-http.service';
import { EmployeeGaTrackingService } from './employee-ga-tracking.service';
import { AccountType } from '~shared/models';
import { AuthService } from './authorization-idam.service';
import { IdentityService } from '~app/core/modules/identity/services/identity.service';
import { AuthorizationStateType } from '../constants/authorization.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { Subject } from 'rxjs';
import { environment } from '~env/environment';
import { ExponeaService } from '~shared/services/exponea.service';
import { AssistedSalesRedirectionURLs } from '../constants/validate-email.constant';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService implements OnDestroy {
  private destroySubject$ = new Subject<void>();
  isExponeaSessionFixEnabled = false;
  constructor(
    private router: Router,
    private accountInfoService: AccountInfoService,
    private authorizationHttpService: AuthorizationHttpService,
    private store$: Store<State>,
    private storageService: StorageService,
    private employeeGaTrackingService: EmployeeGaTrackingService,
    private authService: AuthService,
    private readonly identityService: IdentityService,
    private featureFlagService: ActiveFeaturesConfigcatService,
    private exponeaService: ExponeaService
  ) {
    this.featureFlagService
      .isFeatureEnabledReadyForCurrentCountry$(FeatureToggleEnum.FF_SL_NG_EXPONEA_SESSION_BUGFIX)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((isEnabled: boolean): void => {
        this.isExponeaSessionFixEnabled = isEnabled;
      });
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  impersonateCustomer(
    customerCdmId: string,
    businessCdmId?: string,
    customerEmail?: string,
    hasDigitalIdentity: boolean = false,
    { redirectToCatalog } = { redirectToCatalog: true }
  ) {
    this.clearAddressCheckoutLocalStorage();
    return this.accountInfoService.impersonateCustomer(customerCdmId, businessCdmId, customerEmail).pipe(
      tap(() => {
        if (this.isExponeaSessionFixEnabled) {
          localStorage.setItem(EXPONEA_SESSION_UPDATE_LOCAL_STORAGE_KEY, 'true');
          this.exponeaService.sendNewCustomerData('login', customerEmail);
        }
        if (hasDigitalIdentity) {
          localStorage.setItem(HAS_DIGITAL_IDENTITY_KEY, 'true');
        }

        this.employeeGaTrackingService.gaTrackingImpersonationLogin();
        //saving information to session storage for later use
        sessionStorage.setItem(SESSION_STORAGE_CUSTOMER_CDM_ID_KEY, customerCdmId);
        businessCdmId && sessionStorage.setItem(SESSION_STORAGE_CUSTOMER_BUSINESS_CDM_ID_KEY, businessCdmId);
        localStorage.removeItem(PHONE_NUMBER_LOCAL_STORAGE_KEY);

        if (redirectToCatalog) {
          this.featureFlagService.getFeatureValue(
            FeatureToggleEnum.FF_SNL_CO_5307_ASSISTED_SALES_REDIRECTION_URLS,
          ).subscribe((redirectionURLs) => {
            let assistedSalesRedirectURL: AssistedSalesRedirectionURLs;
            try {
              assistedSalesRedirectURL = JSON.parse(redirectionURLs) as AssistedSalesRedirectionURLs;
            } catch (error) {
              // do nothing and fallback value will be used
            }
            const impersonationRedirectURL = assistedSalesRedirectURL?.impersonationRedirectURL ?? ASSISTED_SALES_LOGIN_REDIRECT_ROUTE

            window.location.href = `${environment.appBaseUrl()}/${impersonationRedirectURL}`;
          });
        }
      })
    );
  }

  stopImpersonatingCustomer(onEnd?: () => void) {
    this.authorizationHttpService
      .impersonationLogout()
      .pipe(
        mergeMap(() => this.accountInfoService.fetchAccountInfo()),
        tap(() => {
          CheckoutCommonUtil.clearTmpDiscountCodeAppliedFromSessionStorage();
          sessionStorage.removeItem(SESSION_STORAGE_CUSTOMER_CDM_ID_KEY);
          sessionStorage.removeItem(SESSION_STORAGE_CUSTOMER_BUSINESS_CDM_ID_KEY);
          localStorage.removeItem(HAS_DIGITAL_IDENTITY_KEY);
          localStorage.removeItem(IMPERSONATED_CUSTOMER_FULLNAME_KEY);
          localStorage.removeItem(PHONE_NUMBER_LOCAL_STORAGE_KEY);
          localStorage.removeItem(ACCOUNT_TYPE_KEY);
          this.clearAddressCheckoutLocalStorage();
          this.employeeGaTrackingService.gaTrackingImpersonationLogout();
        })
      )
      .subscribe(onEnd);
  }

  handleStopImpersonatingCustomer = (e?: Event, onEnd?: () => void) => {
    e?.preventDefault();

    this.stopImpersonatingCustomer(onEnd);
  };

  handleEmployeeLogout() {
    const prevLocalStorage = localStorage.getItem(ACCOUNT_TYPE_KEY);
    return this.authorizationHttpService.impersonationLogout().pipe(
      tap(() => {
        CheckoutCommonUtil.clearTmpDiscountCodeAppliedFromSessionStorage();
        this.clearAddressCheckoutLocalStorage();
        localStorage.removeItem(IMPERSONATED_CUSTOMER_FULLNAME_KEY);
        localStorage.removeItem(PHONE_NUMBER_LOCAL_STORAGE_KEY);
        if (prevLocalStorage === AccountType.EMPLOYEE_AS_CUSTOMER) {
          this.employeeGaTrackingService.gaTrackingImpersonationLogout();
        }
        this.employeeGaTrackingService.gaTrackingEmployeeLogout();
        localStorage.removeItem(ACCOUNT_TYPE_KEY);
        this.authService.logout();
        this.identityService
          .getAuthAccountInfoStatus()
          .pipe(
            filter((status) => status === AuthorizationStateType.LoggedOut),
            take(NUMBER_1)
          )
          .subscribe(() => {
            this.featureFlagService.getFeatureValue(
              FeatureToggleEnum.FF_SNL_CO_5307_ASSISTED_SALES_REDIRECTION_URLS,
            ).subscribe((redirectionURLs) => {
              let assistedSalesRedirectURL: AssistedSalesRedirectionURLs;
              try {
                assistedSalesRedirectURL = JSON.parse(redirectionURLs) as AssistedSalesRedirectionURLs;
              } catch (error) {
                // do nothing and fallback value will be used
              }
              const logoutRedirectURL = assistedSalesRedirectURL?.angularLogoutURL ?? ASSISTED_SALES_LOGIN_REDIRECT_ROUTE

              window.location.href = `${environment.appBaseUrl()}/${logoutRedirectURL}`;
            });
          });
      })
    );
  }

  clearAddressCheckoutLocalStorage() {
    this.store$.dispatch(new ClearAddresses());
    CheckoutCommonUtil.clearTmpAddressesFromLocalStorage();
    this.storageService.local.remove(CHECKOUT_LOCALSTORAGE_KEY);
  }
}
